import { FormEvent, useEffect, useState } from "react";
import {
  Hexagon,
  KeyRound,
  ArrowRight,
  AlertCircle,
  User as UserIcon,
  AtSign,
  Sparkles,
  Shield,
} from "lucide-react";
import { ThreeCanvasHost } from "./os/renderer/ThreeCanvasHost";
import { DesktopChrome } from "./components/DesktopChrome";
import {
  completeInstall,
  fetchInstallStatus,
  fetchPackages,
  login,
  osSocket,
} from "./os/api";
import { osStore } from "./os/store";
import { activateTheme, paintCachedTheme } from "./os/theme-host";
import { startAnalytics } from "./os/analytics";
import { installWallpaperPointer } from "./os/wallpaper-pointer";
import { installTokenGallery } from "./os/token-gallery";

export function OsRoot() {
  useEffect(() => {
    osStore.applyUiScale();
  }, []);
  // Dev command only — `__arclogTokenGallery()` from the console. Installing it
  // renders nothing; the gallery is deliberately not a dock app, a Settings
  // pane or a keyboard chord, so normal use cannot land on it by accident.
  // See the header of token-gallery.ts for why the instrument exists.
  useEffect(() => installTokenGallery(), []);
  // Passive cursor tracking for wallpapers. Installed once for the whole
  // session, not per wallpaper: a mount/unmount cycle should not drop the
  // listeners, and the snapshot is shared anyway.
  useEffect(() => installWallpaperPointer(), []);
  const [phase, setPhase] = useState<"loading" | "install" | "login" | "desktop">("loading");
  const [guestEnabled, setGuestEnabled] = useState(false);
  const [error, setError] = useState("");
  const [booting, setBooting] = useState(false);

  // install form
  const [iUser, setIUser] = useState("admin");
  const [iName, setIName] = useState("Administrator");
  const [iPass, setIPass] = useState("");
  const [iGuest, setIGuest] = useState(false);
  const [iOpenRouter, setIOpenRouter] = useState("");

  // login form
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [useKey, setUseKey] = useState(false);
  const [key, setKey] = useState("");

  useEffect(() => {
    paintCachedTheme();
    void activateTheme(osStore.themeId || "theme.macos-glass").then((vars) => {
      osStore.setTheme(osStore.themeId || "theme.macos-glass", vars);
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const st = await fetchInstallStatus();
        setGuestEnabled(st.guestEnabled);
        if (st.needsInstall) {
          setPhase("install");
          return;
        }
        if (osStore.token) {
          setPhase("desktop");
        } else {
          setPhase("login");
        }
      } catch {
        // offline / old server — fall back to login
        setPhase(osStore.token ? "desktop" : "login");
      }
    })();
  }, []);

  // Begin the visit once the desktop is actually up — i.e. someone signed in.
  // `startAnalytics` is idempotent because StrictMode runs effects twice in
  // development, and a second `session.start` would double every visit count.
  useEffect(() => {
    if (phase !== "desktop" || !osStore.token) return;
    startAnalytics();
  }, [phase]);

  useEffect(() => {
    if (phase !== "desktop" || !osStore.token) return;
    let cancelled = false;
    (async () => {
      setBooting(true);
      try {
        await fetchPackages();
        osSocket.connect();
        // Refresh keeps open windows (sessionStorage). Reboot clears them first.
        const restored = osStore.restoreSessionLayout();
        if (!restored) {
          const perms = osStore.permissions;
          const canTerm =
            perms.includes("hermes.chat") ||
            perms.includes("os.admin") ||
            perms.length === 0;
          // Only auto-open a starter app once per session when layout is empty
          if (canTerm && !sessionStorage.getItem("arclog.openedTerminal")) {
            osStore.openApp("app.terminal", "Terminal");
            sessionStorage.setItem("arclog.openedTerminal", "1");
          } else if (!canTerm && !sessionStorage.getItem("arclog.openedFiles")) {
            osStore.openApp("app.files", "Files");
            sessionStorage.setItem("arclog.openedFiles", "1");
          }
        }
      } catch {
        if (!cancelled) {
          osStore.setToken(null);
          localStorage.removeItem("arclog.user");
          setPhase("login");
          setError("Session expired — log in again");
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase]);

  const onInstall = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await completeInstall({
        username: iUser,
        password: iPass,
        displayName: iName,
        enableGuest: iGuest,
        openRouterKey: iOpenRouter.trim() || undefined,
      });
      setPhase("desktop");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Install failed");
    }
  };

  const onLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      if (useKey) await login(key);
      else await login(username, password);
      setPhase("desktop");
    } catch {
      setError("Invalid credentials");
    }
  };

  const onGuest = async () => {
    setError("");
    try {
      await login("guest");
      setPhase("desktop");
    } catch {
      setError("Guest is disabled");
    }
  };

  return (
    <div className="os-root">
      <ThreeCanvasHost />
      {phase === "loading" && (
        <div className="login-screen">
          <div className="login-card glass glass-strong">
            <div className="login-logo">
              <Hexagon size={32} strokeWidth={2.2} />
            </div>
            <h1>ArcLog OS</h1>
            <p>Starting…</p>
          </div>
        </div>
      )}

      {phase === "install" && (
        <div className="login-screen">
          <form className="login-card glass glass-strong" onSubmit={onInstall} style={{ textAlign: "left" }}>
            <div className="login-logo" style={{ margin: "0 auto 16px" }}>
              <Shield size={32} strokeWidth={2.2} />
            </div>
            <h1 style={{ textAlign: "center" }}>Install ArcLog OS</h1>
            <p style={{ textAlign: "center" }}>Create the first administrator account</p>
            {error && (
              <div className="error">
                <AlertCircle size={14} /> {error}
              </div>
            )}
            <label className="login-label" htmlFor="inst-name">
              Display name
            </label>
            <div className="login-field">
              <UserIcon className="field-icon" size={16} strokeWidth={2} />
              <input
                id="inst-name"
                value={iName}
                onChange={(e) => setIName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <label className="login-label" htmlFor="inst-user">
              Username
            </label>
            <div className="login-field">
              <AtSign className="field-icon" size={16} strokeWidth={2} />
              <input
                id="inst-user"
                value={iUser}
                onChange={(e) => setIUser(e.target.value)}
                required
              />
            </div>
            <label className="login-label" htmlFor="inst-pass">
              Password
            </label>
            <div className="login-field">
              <KeyRound className="field-icon" size={16} strokeWidth={2} />
              <input
                id="inst-pass"
                type="password"
                value={iPass}
                onChange={(e) => setIPass(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <label className="login-label" htmlFor="inst-openrouter">
              OpenRouter API key
              <span className="login-label-opt">Optional</span>
            </label>
            <div className="login-field">
              <Sparkles className="field-icon" size={16} strokeWidth={2} />
              <input
                id="inst-openrouter"
                type="password"
                value={iOpenRouter}
                onChange={(e) => setIOpenRouter(e.target.value)}
                placeholder="sk-or-…"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <p className="login-hint">
              Powers AI in Studio &amp; Hermes. Skip it now — you can add or change it
              anytime in Settings → OpenRouter.
            </p>
            <label
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                margin: "10px 0 14px",
                fontSize: 13,
                opacity: 0.85,
              }}
            >
              <input type="checkbox" checked={iGuest} onChange={(e) => setIGuest(e.target.checked)} />
              Enable Guest account (locked down)
            </label>
            <button type="submit">
              Install & Enter <ArrowRight size={16} />
            </button>
          </form>
        </div>
      )}

      {phase === "login" && (
        <div className="login-screen">
          <form className="login-card glass glass-strong" onSubmit={onLogin}>
            <div className="login-logo">
              <Hexagon size={32} strokeWidth={2.2} />
            </div>
            <h1>ArcLog OS</h1>
            <p>Sign in to your desktop</p>
            {error && (
              <div className="error">
                <AlertCircle size={14} /> {error}
              </div>
            )}
            {!useKey ? (
              <>
                <div className="login-field">
                  <UserIcon className="field-icon" size={16} strokeWidth={2} />
                  <input
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="login-field">
                  <KeyRound className="field-icon" size={16} strokeWidth={2} />
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className="login-field">
                <KeyRound className="field-icon" size={16} strokeWidth={2} />
                <input
                  type="password"
                  placeholder="Recovery dashboard key"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  autoFocus
                />
              </div>
            )}
            <button type="submit">
              Enter <ArrowRight size={16} strokeWidth={2.25} />
            </button>
            <div
              style={{
                marginTop: 12,
                display: "flex",
                gap: 10,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              {guestEnabled && (
                <button
                  type="button"
                  onClick={onGuest}
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,.2)",
                    color: "inherit",
                    borderRadius: 8,
                    padding: "8px 12px",
                    cursor: "pointer",
                  }}
                >
                  Continue as Guest
                </button>
              )}
              <button
                type="button"
                onClick={() => setUseKey((v) => !v)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "rgba(255,255,255,.55)",
                  cursor: "pointer",
                  fontSize: 12,
                  textDecoration: "underline",
                }}
              >
                {useKey ? "Use username" : "Recovery key"}
              </button>
            </div>
          </form>
        </div>
      )}

      {phase === "desktop" && (
        <>
          <DesktopChrome />
          {booting && (
            <div className="toasts">
              <div className="toast glass glass-strong">Booting ArcLog OS…</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
